import { useState, useEffect } from 'react';
import offlineDB from '../utils/offlineDB';
import logger from '@/utils/logger';

// Hook para gestionar datos offline
export const useOfflineData = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [cacheStats, setCacheStats] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cargar stats del cache
    loadCacheStats();

    // Escuchar mensajes del Service Worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleSWMessage);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleSWMessage);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: handleSWMessage
  }, []);

  const handleSWMessage = (event) => {
    const { type, timestamp } = event.data;
    
    if (type === 'SYNC_COMPLETE') {
      logger.log('✅ Background sync completado:', timestamp);
      setLastSyncTime(timestamp);
      loadCacheStats();
    }
    
    if (type === 'REQUEST_SYNC') {
      logger.log('🔄 Service Worker solicita sincronización');
      // Esto se maneja en App.js con el listener de 'online'
    }
  };

  const loadCacheStats = async () => {
    try {
      const stats = await offlineDB.getCacheStats();
      setCacheStats(stats);
    } catch (error) {
      console.error('Error cargando stats de cache:', error);
    }
  };

  // Cachear biblioteca completa
  const cacheSongs = async (songs) => {
    try {
      await offlineDB.saveSongs(songs);
      await offlineDB.saveUserData('lastCacheUpdate', new Date().toISOString());
      await loadCacheStats();
      logger.log('✅ Biblioteca cacheada exitosamente');
      return true;
    } catch (error) {
      console.error('❌ Error cacheando biblioteca:', error);
      return false;
    }
  };

  // Cachear eventos
  const cachePlaylists = async (playlists) => {
    try {
      await offlineDB.savePlaylists(playlists);
      await loadCacheStats();
      logger.log('✅ Eventos cacheados exitosamente');
      return true;
    } catch (error) {
      console.error('❌ Error cacheando eventos:', error);
      return false;
    }
  };

  // Obtener datos (offline-first)
  const getSongs = async (fetchFromAPI) => {
    try {
      if (isOnline) {
        // Si estamos online, traer del API y actualizar cache
        const apiSongs = await fetchFromAPI();
        await cacheSongs(apiSongs);
        return apiSongs;
      } else {
        // Si estamos offline, traer del cache
        logger.log('🔴 Offline - Usando datos cacheados');
        const cachedSongs = await offlineDB.getAllSongs();
        if (cachedSongs.length === 0) {
          throw new Error('No hay datos cacheados disponibles');
        }
        return cachedSongs;
      }
    } catch (error) {
      console.error('Error obteniendo canciones:', error);
      // Intentar cache como fallback
      const cachedSongs = await offlineDB.getAllSongs();
      if (cachedSongs.length > 0) {
        logger.log('⚠️ Usando cache como fallback');
        return cachedSongs;
      }
      throw error;
    }
  };

  const getPlaylists = async (fetchFromAPI) => {
    try {
      if (isOnline) {
        const apiPlaylists = await fetchFromAPI();
        await cachePlaylists(apiPlaylists);
        return apiPlaylists;
      } else {
        logger.log('🔴 Offline - Usando eventos cacheados');
        const cachedPlaylists = await offlineDB.getAllPlaylists();
        if (cachedPlaylists.length === 0) {
          throw new Error('No hay eventos cacheados disponibles');
        }
        return cachedPlaylists;
      }
    } catch (error) {
      console.error('Error obteniendo eventos:', error);
      const cachedPlaylists = await offlineDB.getAllPlaylists();
      if (cachedPlaylists.length > 0) {
        logger.log('⚠️ Usando cache como fallback');
        return cachedPlaylists;
      }
      throw error;
    }
  };

  // Sincronizar cambios pendientes
  const syncPendingChanges = async (apiClient) => {
    if (!isOnline) {
      logger.log('⚠️ Sin conexión - No se puede sincronizar');
      return false;
    }

    setIsSyncing(true);
    try {
      const queue = await offlineDB.getSyncQueue();
      
      if (queue.length === 0) {
        logger.log('✅ No hay cambios pendientes');
        setIsSyncing(false);
        return true;
      }

      logger.log(`🔄 Sincronizando ${queue.length} cambios...`);

      let successCount = 0;
      let errorCount = 0;

      for (const item of queue) {
        try {
          // Ejecutar la acción según el tipo
          switch (item.type) {
            case 'UPDATE_SONG':
              await apiClient.put(`/songs/${item.data.id}`, item.data);
              break;
              
            case 'UPDATE_RATING':
              await apiClient.put(`/songs/${item.data.id}`, { rating: item.data.rating });
              break;
              
            case 'TOGGLE_FAVORITE':
              await apiClient.put(`/songs/${item.data.id}`, { favorito: item.data.favorito });
              break;

            case 'ADD_PETICION':
              await apiClient.post(`/playlists/${item.data.playlistId}/peticiones`, {
                peticion: item.data.peticion,
              });
              break;
              
            default:
              console.warn('Tipo de acción desconocido:', item.type);
          }

          // Marcar como sincronizado
          await offlineDB.markAsSynced(item.id);
          successCount++;
          logger.log(`✅ Sincronizado: ${item.type}`);
        } catch (error) {
          errorCount++;
          console.error(`❌ Error sincronizando ${item.type}:`, error);
          // Continuar con el siguiente item
        }
      }

      await loadCacheStats();
      setIsSyncing(false);
      
      if (errorCount === 0) {
        logger.log(`✅ Sincronización completa: ${successCount} cambios`);
      } else {
        logger.log(`⚠️ Sincronización parcial: ${successCount} OK, ${errorCount} errores`);
      }
      
      return errorCount === 0;
    } catch (error) {
      console.error('❌ Error en sincronización:', error);
      setIsSyncing(false);
      return false;
    }
  };

  // Agregar acción a la cola (para ediciones offline)
  const queueOfflineAction = async (type, data) => {
    try {
      await offlineDB.addToSyncQueue({ type, data });
      await loadCacheStats();
      logger.log(`📝 Acción ${type} agregada a cola de sincronización`);
      return true;
    } catch (error) {
      console.error('Error agregando acción a cola:', error);
      return false;
    }
  };

  // Limpiar todo el cache
  const clearCache = async () => {
    try {
      await offlineDB.clearAllData();
      await loadCacheStats();
      logger.log('🗑️ Cache limpiado');
      return true;
    } catch (error) {
      console.error('Error limpiando cache:', error);
      return false;
    }
  };

  // Registrar Background Sync
  const registerBackgroundSync = async () => {
    if ('serviceWorker' in navigator && 'sync' in ServiceWorkerRegistration.prototype) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.sync.register('sync-offline-changes');
        logger.log('✅ Background Sync registrado');
        return true;
      } catch (error) {
        console.error('❌ Error registrando Background Sync:', error);
        return false;
      }
    } else {
      logger.log('⚠️ Background Sync no soportado en este navegador');
      return false;
    }
  };

  // Precargar próximos eventos
  const precacheUpcomingEvents = async (playlists) => {
    if (!('serviceWorker' in navigator)) return;

    try {
      // Filtrar próximos 7 días
      const today = new Date();
      const nextWeek = new Date(today);
      nextWeek.setDate(nextWeek.getDate() + 7);

      const upcomingEvents = playlists
        .filter(p => {
          if (!p.fecha_evento) return false;
          const eventDate = new Date(p.fecha_evento);
          return eventDate >= today && eventDate <= nextWeek;
        })
        .map(p => p.id);

      if (upcomingEvents.length === 0) {
        logger.log('📅 No hay eventos próximos para precargar');
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      const messageChannel = new MessageChannel();

      registration.active.postMessage(
        {
          type: 'PRECACHE_EVENTS',
          eventIds: upcomingEvents
        },
        [messageChannel.port2]
      );

      messageChannel.port1.onmessage = (event) => {
        if (event.data.success) {
          logger.log(`✅ ${upcomingEvents.length} eventos próximos precargados`);
        }
      };
    } catch (error) {
      console.error('Error precargando eventos:', error);
    }
  };

  // Obtener métricas offline
  const getOfflineMetrics = async () => {
    if (!('serviceWorker' in navigator)) return null;

    try {
      const registration = await navigator.serviceWorker.ready;
      const messageChannel = new MessageChannel();

      registration.active.postMessage(
        { type: 'GET_METRICS' },
        [messageChannel.port2]
      );

      return new Promise((resolve) => {
        messageChannel.port1.onmessage = (event) => {
          resolve(event.data);
        };
      });
    } catch (error) {
      console.error('Error obteniendo métricas:', error);
      return null;
    }
  };

  return {
    isOnline,
    cacheStats,
    isSyncing,
    lastSyncTime,
    cacheSongs,
    cachePlaylists,
    getSongs,
    getPlaylists,
    syncPendingChanges,
    queueOfflineAction,
    clearCache,
    loadCacheStats,
    registerBackgroundSync,
    precacheUpcomingEvents,
    getOfflineMetrics
  };
};
